import { Link } from "@tanstack/react-router";

import Breadcrumbs from "@/components/breadcrumbs";
import DownloadApp from "@/components/download-app";
import Faq from "@/components/faq";
import Header from "@/components/header-v2";
import { Button } from "@/components/ui/button";

import Games from "./games";
import RelatedCities from "./related-cities";
import TextSection from "./text-section";
import type {
  FaqType,
  GameType,
  RelatedCityType,
  TextSectionType,
} from "./types";

export interface CityProps {
  about: TextSectionType | null;
  coordinates: number[];
  description: string;
  faq: FaqType[];
  findRooms: TextSectionType | null;
  games: GameType[];
  guide: TextSectionType | null;
  howDoEscapeRoomsWork: TextSectionType | null;
  howToBook: TextSectionType | null;
  learnMore: TextSectionType | null;
  nameLong: string;
  nameShort: string;
  relatedCities: RelatedCityType[];
  slug: string;
  subtitle: string;
  title: string;
}

export default function City({
  about,
  coordinates,
  description,
  faq,
  findRooms,
  games,
  guide,
  howDoEscapeRoomsWork,
  howToBook,
  learnMore,
  nameLong,
  nameShort,
  relatedCities,
  slug,
  subtitle,
  title,
}: CityProps) {
  return (
    <>
      <Header variant="simplified" />
      <div className="hidden md:block">
        <Breadcrumbs
          breadcrumbs={[
            { name: "Cities", url: "/cities" },
            { name: nameShort, url: `/city/${slug}` },
          ]}
          className="pt-[5px]"
          title={title}
        />

        {!!subtitle && (
          <h2 className="mx-auto mt-3 max-w-180 text-center font-normal text-16/[1.875em] text-pale-lavender">
            {subtitle}
          </h2>
        )}
      </div>
      <Games games={games} />
      <div className="flex pt-5.5 md:w-1/2 md:justify-center">
        <Button
          asChild
          className="mx-auto h-[50px] w-44 rounded-[25px]"
          variant="cta"
        >
          <Link
            to={`/explore/${encodeURIComponent(nameLong)}?location=${encodeURIComponent(
              coordinates.join(",")
            )}`}
          >
            See all games
          </Link>
        </Button>
      </div>
      {!!relatedCities.length && <RelatedCities cities={relatedCities} />}
      <div className="container">
        {!!description && (
          <TextSection body={description} header={`About ${nameShort}`} />
        )}
        {!!about && <TextSection {...about} />}
        {!!learnMore && <TextSection {...learnMore} />}
        <DownloadApp className="pt-[247px] md:pt-[276px] md:pb-[183px]" />
        {!!howDoEscapeRoomsWork && <TextSection {...howDoEscapeRoomsWork} />}
        {!!findRooms && <TextSection {...findRooms} />}
        {!!faq.length && (
          <section className="pt-20 md:pt-35">
            <Faq items={faq} />
          </section>
        )}
        {!!howToBook && <TextSection {...howToBook} />}
        {!!guide && <TextSection {...guide} />}
        <DownloadApp className="pt-[247px] pb-20 md:pt-[276px] md:pb-70" />
      </div>
    </>
  );
}
